import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// ── Workspace display timezone ───────────────────────────────────────────
//
// The app used to hardcode `America/Chicago` everywhere a full timestamp was
// displayed. Task #251 makes the *display* timezone a per-workspace setting.
// Storage stays UTC; only formatting changes.
//
// We keep the resolved timezone in a module-level variable so the existing
// pure formatter helpers (`formatLocalDate`, etc.) don't need a tz argument
// threaded through every call site. `useWorkspaceTimezone()` fetches the
// workspace's configured zone once and pushes it here. The default is
// `America/Chicago` so unset workspaces (and the pre-fetch window) behave
// exactly as before.
export const DEFAULT_WORKSPACE_TIMEZONE = "America/Chicago";

let currentWorkspaceTimeZone = DEFAULT_WORKSPACE_TIMEZONE;

/** Returns the workspace's configured display timezone (IANA string). */
export function getWorkspaceTimeZone(): string {
  return currentWorkspaceTimeZone;
}

/**
 * Sets the active workspace display timezone. Called by `useWorkspaceTimezone`
 * once the setting loads. Invalid/empty values fall back to the default so a
 * bad value can never break date rendering app-wide.
 */
export function setWorkspaceTimeZone(tz: string | null | undefined): void {
  if (!tz) {
    currentWorkspaceTimeZone = DEFAULT_WORKSPACE_TIMEZONE;
    return;
  }
  try {
    // Validate via Intl — throws on a bogus IANA string.
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    currentWorkspaceTimeZone = tz;
  } catch {
    currentWorkspaceTimeZone = DEFAULT_WORKSPACE_TIMEZONE;
  }
}

/**
 * Returns the short timezone abbreviation for the workspace zone at the given
 * instant (e.g. "CST"/"CDT", "EST"/"EDT", "PST"/"PDT"). DST-aware. Use this
 * instead of hardcoding a "CT" suffix.
 */
export function getTimeZoneAbbreviation(
  date: Date | string | number = new Date(),
  tz: string = getWorkspaceTimeZone(),
): string {
  const d = typeof date === "string" || typeof date === "number" ? new Date(date) : date;
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    }).formatToParts(d);
    const name = parts.find((p) => p.type === "timeZoneName")?.value;
    return name ?? "";
  } catch {
    return "";
  }
}

/**
 * Returns a Date whose LOCAL calendar fields (year/month/day) match the current
 * wall-clock calendar day in the workspace's configured timezone, anchored at
 * local noon. Use this as the "now" seed for calendar-day range math (date-fns
 * startOfDay / startOfWeek / subDays + format "yyyy-MM-dd") so presets like
 * Today / This Week / Last 7 Days align to the workspace day boundary — which is
 * how shift work_date is stamped — instead of the viewer's browser timezone.
 * Anchored at noon so DST transitions never roll the day over.
 */
export function workspaceCalendarNow(tz: string = getWorkspaceTimeZone()): Date {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date());
    const y = Number(parts.find((p) => p.type === "year")?.value);
    const m = Number(parts.find((p) => p.type === "month")?.value);
    const d = Number(parts.find((p) => p.type === "day")?.value);
    if (y && m && d) return new Date(y, m - 1, d, 12, 0, 0);
  } catch {
    // fall through to browser-local now
  }
  return new Date();
}

/**
 * Formats a date safely without timezone-induced day shifts.
 *
 * The problem: `new Date("2026-03-15")` parses as UTC midnight. When
 * converted to Central Time (UTC-5/6) it becomes "March 14 at 7pm",
 * so toLocaleDateString shows March 14 instead of March 15.
 *
 * Fix: date-only strings (YYYY-MM-DD) or timestamps at midnight UTC
 * are parsed by their components and set to local noon to stay on the
 * correct calendar day regardless of the viewer's timezone.
 * Full timestamps are shown in the workspace's configured timezone.
 */
export function formatLocalDate(
  date: Date | string | null | undefined,
  opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" }
): string {
  if (!date) return "—";
  const dateStr = typeof date === "string" ? date : date.toISOString();

  // Date-only strings or timestamps that are exactly at UTC midnight
  const isDateOnly =
    /^\d{4}-\d{2}-\d{2}$/.test(dateStr) ||
    /^\d{4}-\d{2}-\d{2}T00:00:00/.test(dateStr);

  if (isDateOnly) {
    const [year, month, day] = dateStr.split("T")[0].split("-").map(Number);
    // Create at local noon to survive DST transitions
    return new Date(year, month - 1, day, 12, 0, 0).toLocaleDateString("en-US", opts);
  }

  // Full timestamps → display in the workspace's configured timezone.
  return new Date(dateStr).toLocaleDateString("en-US", {
    ...opts,
    timeZone: getWorkspaceTimeZone(),
  });
}

/**
 * Formats a full timestamp (date + time) in the workspace's configured
 * timezone. Unlike `formatLocalDate`, this is always treated as an instant —
 * never a calendar-day — so use it only for true timestamps.
 */
export function formatLocalDateTime(
  date: Date | string | null | undefined,
  opts: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }
): string {
  if (!date) return "—";
  return new Date(date).toLocaleString("en-US", {
    ...opts,
    timeZone: getWorkspaceTimeZone(),
  });
}

/**
 * Formats just the time-of-day of an instant in the workspace's configured
 * timezone.
 */
export function formatLocalTime(
  date: Date | string | null | undefined,
  opts: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" }
): string {
  if (!date) return "—";
  return new Date(date).toLocaleTimeString("en-US", {
    ...opts,
    timeZone: getWorkspaceTimeZone(),
  });
}

/**
 * Normalizes a string for punctuation-insensitive search.
 * Strips periods, apostrophes, hyphens, and collapses extra whitespace,
 * then lowercases. Use on both the query and the target so that
 * "st louis" matches "St. Louis", "ofallon" matches "O'Fallon", etc.
 */
export function normalizeSearchStr(s: string): string {
  return s
    .replace(/[.\-']/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

export function formatPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return "";
  const cleaned = String(phone).trim().replace(/[^\d]/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned[0] === "1") {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  if (cleaned.length === 7) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  }
  return phone;
}
